// LogStore: central structured logging with optional dev-mode buffer
import { makeAutoObservable } from "mobx";

// Coarse, low-frequency tags from realtime.service.js that are safe/useful to ship to the
// backend for later admin investigation. Raw per-token streaming deltas are deliberately
// excluded — they're high-volume and not useful for debugging connection/tool-call issues.
const SHIPPABLE_TAGS = new Set([
  "OpenStart", "PeerConnectionStateChange", "DataChannelOpen", "DataChannelClose",
  "ScheduleReconnect", "ReconnectTimerFired", "Reconnect", "RelayNoCallId",
  "ScheduleReconnectOnPeerState", "RelayScheduleReconnect", "RelayClosed",
  "OpenError", "RelayError", "ServiceError", "NaturalWrapError", "QuickWrapError",
  "FunctionCallFromResponseDoneError", "DataChannelError", "ReadyTimeout", "RelaySendError",
  "FunctionCall", "FunctionCallFromResponseDone",
  "SessionUpdated", "NaturalWrap", "QuickWrap", "T20Warning", "WrapWarningSkippedActiveResponse",
  "SendEvent",
  "UnvoicedTextDetected", "UnvoicedTextSkipped", "UnvoicedTextRevoiceExhausted",
]);

// RecvEvent fires for every inbound data-channel/relay message, including per-token deltas.
// Only ship the milestone event types; drop streaming deltas.
const RECV_EVENT_TYPE_ALLOWLIST = new Set([
  "session.updated", "response.created", "response.done", "response.output_item.done", "error",
  // Output/input audio lifecycle — load-bearing for auto-mute and the output state machine.
  // Ship them so stuck-mic / caption issues are diagnosable from the persisted trail.
  "output_audio_buffer.started", "output_audio_buffer.stopped",
  "input_audio_buffer.speech_started", "input_audio_buffer.speech_stopped",
  // A text-modality (unvoiced) turn — the signature of the silent-question glitch.
  "response.output_text.done",
]);

function isShippableRecvEvent(payload) {
  const type = payload?.type;
  if (typeof type !== "string") return false;
  if (RECV_EVENT_TYPE_ALLOWLIST.has(type)) return true;
  return /\.completed$/.test(type) && type.startsWith("conversation.item.");
}

/**
 * @param {string} tag
 * @param {any} payload
 */
export function shouldShipLogEntry(tag, payload) {
  if (tag === "RecvEvent") return isShippableRecvEvent(payload);
  return SHIPPABLE_TAGS.has(tag);
}

const MAX_PAYLOAD_SERIALIZED_CHARS = 10000;

function serializablePayload(payload) {
  // Some tags carry raw Error/Event objects which JSON.stringify can't usefully serialize.
  if (payload instanceof Error) return { message: payload.message, code: payload.code };
  if (typeof Event !== "undefined" && payload instanceof Event) return { type: payload.type };
  return payload;
}

/**
 * Guarantees a JSON-serializable value, capped in size, suitable for shipping as a
 * pre-serialized JSON string in a GraphQL variable.
 * @param {any} payload
 */
export function sanitizeLogPayload(payload) {
  let safe;
  try {
    safe = serializablePayload(payload);
    const json = JSON.stringify(safe ?? null);
    if (json.length > MAX_PAYLOAD_SERIALIZED_CHARS) {
      return { truncated: true, originalLength: json.length };
    }
    return safe;
  } catch {
    return { unserializable: true };
  }
}

export class LogStore {
  constructor({ maxEntries = 1000 } = {}) {
    this.entries = [];
    this.maxEntries = maxEntries;
    this.devMode = false;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setDevMode(on) { this.devMode = !!on; }

  add(entry) {
    if (!this.devMode) return; // only buffer in dev mode
    this.entries.push({ ...entry, id: crypto.randomUUID?.() || String(Date.now()) });
    if (this.entries.length > this.maxEntries) this.entries.shift();
  }

  clear() { this.entries = []; }
}
